import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Tenant, TenantBillingStatus, BillingSubscription } from '@kawai/shared';
import { Button, Chip, ZebraTable } from '@kawai/ui';
import { useTenants } from '../lib/hooks/useTenants.js';
import { useBillingSubscriptions } from '../lib/hooks/useBillingSubscriptions.js';
import { useBillingStartSubscription } from '../lib/hooks/useBillingStartSubscription.js';
import { useBillingCancelSubscription } from '../lib/hooks/useBillingCancelSubscription.js';
import { useBillingResumeSubscription } from '../lib/hooks/useBillingResumeSubscription.js';
import { billingStartSubscriptionErrorMessage } from '../lib/api/billingStartSubscription.js';
import { billingCancelSubscriptionErrorMessage } from '../lib/api/billingCancelSubscription.js';
import { billingResumeSubscriptionErrorMessage } from '../lib/api/billingResumeSubscription.js';
import { formatCardLabel, formatYen } from '@kawai/shared';
import { billingStatusChipTone } from '../lib/billingStyles.js';
import { useAuth } from '../lib/auth.js';
import { CARD, MONO, ROW_BTN_BRAND, ROW_BTN_DANGER } from '../lib/uiClasses.js';
import { PageHeader } from '../components/PageHeader.js';
import { Notice } from '../components/Notice.js';

/** 「利用開始」が可能な billingStatus（初期費用決済済み）。 */
const STARTABLE: TenantBillingStatus = 'initial_paid';

/**
 * TP-22 / TP-23: 月額サブスク管理ページ（saas-admin 運営者ビュー）。
 *
 * - tenants 全件 + billing_subscriptions 全件を fetch して join
 * - 操作（排他）:
 *   - 利用開始: billingStatus==='initial_paid'（TP-22）
 *   - 解約:     subscription.status==='active'（稼働中 / past_due も PAY.JP status は active）（TP-23）
 *   - 再開:     subscription.status==='canceled' かつ endedAt 未設定（解約予約中・期末前）（TP-23）
 * - 解約は期末まで利用可（契約「解約月満額・日割なし」）。tenants.billingStatus は期末の
 *   subscription.deleted Webhook で 'canceled' に確定する（本ページの解約直後は active のまま）。
 *
 * 注: カード登録はテナント自身が tenant-portal /billing で行う（TP-20）。
 * 各行の tenantId から決済明細（カード / 請求履歴）ページ /billing/:tenantId へドリルインできる（TP-25）。
 * S-137: 見た目は docs/UI-GUIDE.md の v2（白カード 30/26・ZebraTable・Chip・行内ボタンは面の色差）。
 */
export function BillingPage() {
  const { email, signOut } = useAuth();
  const { data, isLoading, error, refetch, isFetching } = useTenants();
  const { data: subs } = useBillingSubscriptions();
  const start = useBillingStartSubscription();
  const cancel = useBillingCancelSubscription();
  const resume = useBillingResumeSubscription();

  const [busyTenantId, setBusyTenantId] = useState<string | null>(null);
  const [opError, setOpError] = useState<string | null>(null);
  const [opNotice, setOpNotice] = useState<{
    kind: 'ok' | 'warn';
    text: string;
  } | null>(null);

  const subByTenant = useMemo(() => {
    const m = new Map<string, BillingSubscription>();
    for (const s of subs ?? []) m.set(s.tenantId, s);
    return m;
  }, [subs]);

  const anyPending = start.isPending || cancel.isPending || resume.isPending;

  // 開始/解約/再開で共通の「状態リセット → 実行 → notice/error → busy 解除」枠
  // （三重コピペ解消 / レビュー指摘）。confirm 文言は操作ごとに異なるため各 handler に残す。
  async function runTenantOp(
    tenantId: string,
    op: () => Promise<{ kind: 'ok' | 'warn'; text: string }>,
    toErrorMessage: (err: unknown) => string,
  ): Promise<void> {
    if (anyPending) return;
    setOpError(null);
    setOpNotice(null);
    setBusyTenantId(tenantId);
    try {
      setOpNotice(await op());
    } catch (err) {
      setOpError(toErrorMessage(err));
    } finally {
      setBusyTenantId(null);
    }
  }

  async function handleStart(tenant: Tenant) {
    if (tenant.billingStatus !== STARTABLE) return;
    if (anyPending) return;
    const confirmed = window.confirm(
      `テナント "${tenant.id}" の月額サブスクを開始します。\n\n` +
        '・初期費用の決済が成功したテナントのみ開始できます\n' +
        '・初月は利用開始日〜当月末日の日割りで都度課金します\n' +
        '・翌月以降は暦月全額の定額課金になります\n\n' +
        '続行しますか？',
    );
    if (!confirmed) return;
    await runTenantOp(
      tenant.id,
      async () => {
        const res = await start.mutateAsync({ tenantId: tenant.id });
        const yen = formatYen(res.prorationAmountYen);
        if (res.prorationResult === 'charged') {
          return {
            kind: 'ok',
            text: `${tenant.id} の月額を開始しました（初月日割り ${yen} を課金）。`,
          };
        }
        if (res.prorationResult === 'skipped') {
          return {
            kind: 'ok',
            text: `${tenant.id} の月額を開始しました（初月日割りは最小課金額未満のため課金なし）。`,
          };
        }
        return {
          kind: 'warn',
          text: `${tenant.id} の月額を開始しましたが、初月日割り（${yen}）の決済に失敗しました。カードをご確認のうえ別途対応してください。`,
        };
      },
      billingStartSubscriptionErrorMessage,
    );
  }

  async function handleCancel(tenant: Tenant) {
    if (anyPending) return;
    const confirmed = window.confirm(
      `テナント "${tenant.id}" の月額サブスクを解約します。\n\n` +
        '・解約後も現在の課金期間の終了日（期末）まで利用できます\n' +
        '・解約月は満額（日割りなし）です\n' +
        '・次回更新は行われず、期末でサービスが停止します\n' +
        '・期末前であれば「再開」で取り消せます\n\n' +
        '続行しますか？',
    );
    if (!confirmed) return;
    await runTenantOp(
      tenant.id,
      async () => {
        const res = await cancel.mutateAsync({ tenantId: tenant.id });
        const until = res.currentPeriodEnd ? res.currentPeriodEnd.slice(0, 10) : null;
        return {
          kind: 'ok',
          text: `${tenant.id} を解約しました${until ? `（${until} まで利用可）` : ''}。`,
        };
      },
      billingCancelSubscriptionErrorMessage,
    );
  }

  async function handleResume(tenant: Tenant) {
    if (anyPending) return;
    const confirmed = window.confirm(
      `テナント "${tenant.id}" の月額サブスクを再開します。\n\n` +
        '・解約予約を取り消し、月額課金を継続します\n\n' +
        '続行しますか？',
    );
    if (!confirmed) return;
    await runTenantOp(
      tenant.id,
      async () => {
        await resume.mutateAsync({ tenantId: tenant.id });
        return { kind: 'ok', text: `${tenant.id} の月額を再開しました。` };
      },
      billingResumeSubscriptionErrorMessage,
    );
  }

  const tenants = useMemo<Tenant[]>(() => data ?? [], [data]);

  return (
    <main className="mx-auto max-w-[1100px] p-6">
      <PageHeader
        title="課金管理"
        description="月額サブスクの開始 / 解約 / 再開（saas-admin）"
        email={email}
        links={[
          { to: '/', label: 'テナント一覧' },
          { to: '/billing/bank-transfer', label: '振込・口座振替' },
          { to: '/audit-logs', label: '監査ログ' },
        ]}
        onSignOut={() => {
          void signOut();
        }}
      />

      <section className={`mt-6 ${CARD}`} aria-label="月額サブスク">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <p className="max-w-3xl text-label text-ink-3">
            「利用開始」は初期費用の決済が成功（initial_paid）したテナントのみ。「解約」は期末まで利用可
            （解約月満額・日割なし）、期末前なら「再開」で取り消せます。カード登録は各テナントが行います
            （tenant-portal）。
          </p>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              void refetch();
            }}
            disabled={isFetching}
          >
            {isFetching ? '取得中…' : '再取得'}
          </Button>
        </div>

        {isLoading && (
          <p role="status" aria-live="polite" className="mt-4 text-body text-ink-3">
            読み込み中…
          </p>
        )}

        {error && (
          <Notice role="alert" className="mt-4">
            テナント一覧の取得に失敗しました: {error.message}
          </Notice>
        )}

        {opError && (
          <Notice role="alert" className="mt-4">
            {opError}
          </Notice>
        )}

        {opNotice && (
          <Notice
            role="status"
            aria-live="polite"
            tone={opNotice.kind === 'warn' ? 'warning' : 'success'}
            className="mt-4"
          >
            {opNotice.text}
          </Notice>
        )}

        {!isLoading && !error && (
          <div className="mt-4 overflow-x-auto">
            <ZebraTable aria-label="テナント別の課金状態">
              <thead>
                <ZebraTable.HeadRow>
                  <ZebraTable.Th edge="first">tenantId</ZebraTable.Th>
                  <ZebraTable.Th>name</ZebraTable.Th>
                  <ZebraTable.Th>課金状態</ZebraTable.Th>
                  <ZebraTable.Th>カード</ZebraTable.Th>
                  <ZebraTable.Th edge="last">操作</ZebraTable.Th>
                </ZebraTable.HeadRow>
              </thead>
              <tbody>
                {tenants.length === 0 ? (
                  <ZebraTable.Row>
                    <ZebraTable.Td colSpan={5} align="center" tone="muted" padding="lg">
                      テナントがありません
                    </ZebraTable.Td>
                  </ZebraTable.Row>
                ) : (
                  tenants.map((t) => {
                    const status = t.billingStatus ?? 'none';
                    const sub = subByTenant.get(t.id);
                    const startable = status === STARTABLE;
                    // 解約可 = 稼働中。past_due でも PAY.JP subscription.status は active のまま。
                    // trial も active 同様の稼働として扱い canCancelSubscription（サーバ）と整合させる
                    // （paused は pause Callable 未実装で発生しないため UI 非対応 / レビュー指摘）。
                    const cancelable =
                      sub?.status === 'active' || sub?.status === 'trial';
                    // 解約予約中（期末前 / 未削除）→ 再開可。
                    const reservedCancel = sub?.status === 'canceled' && !sub?.endedAt;
                    const periodEnd = sub?.currentPeriodEnd
                      ? sub.currentPeriodEnd.slice(0, 10)
                      : null;
                    const busy = busyTenantId === t.id && anyPending;
                    return (
                      <ZebraTable.Row key={t.id} className="align-top">
                        <ZebraTable.Td className={MONO}>
                          <Link
                            to={`/billing/${t.id}`}
                            className="text-ink underline underline-offset-2"
                            title={`${t.id} の決済明細（カード / 請求履歴）を表示`}
                          >
                            {t.id}
                          </Link>
                        </ZebraTable.Td>
                        <ZebraTable.Td>{t.name}</ZebraTable.Td>
                        <ZebraTable.Td>
                          <Chip tone={billingStatusChipTone(status)}>{status}</Chip>
                          {reservedCancel && (
                            <div className="mt-1 text-note text-warning-ink">
                              解約予約{periodEnd ? `（${periodEnd} まで利用可）` : ''}
                            </div>
                          )}
                        </ZebraTable.Td>
                        <ZebraTable.Td>
                          {t.payjpDefaultCard
                            ? formatCardLabel(t.payjpDefaultCard)
                            : '未登録'}
                        </ZebraTable.Td>
                        <ZebraTable.Td>
                          {startable && (
                            <button
                              type="button"
                              onClick={() => {
                                void handleStart(t);
                              }}
                              disabled={anyPending}
                              aria-label={`${t.id} の月額サブスクを開始`}
                              title="月額サブスクを開始します（初月は日割り課金）"
                              className={ROW_BTN_BRAND}
                            >
                              {busy ? '処理中…' : '利用開始'}
                            </button>
                          )}
                          {cancelable && (
                            <button
                              type="button"
                              onClick={() => {
                                void handleCancel(t);
                              }}
                              disabled={anyPending}
                              aria-label={`${t.id} の月額サブスクを解約`}
                              title="月額サブスクを解約します（期末まで利用可 / 期末前なら再開可）"
                              className={ROW_BTN_DANGER}
                            >
                              {busy ? '処理中…' : '解約'}
                            </button>
                          )}
                          {reservedCancel && (
                            <button
                              type="button"
                              onClick={() => {
                                void handleResume(t);
                              }}
                              disabled={anyPending}
                              aria-label={`${t.id} の月額サブスクを再開`}
                              title="解約予約を取り消して月額を継続します"
                              className={ROW_BTN_BRAND}
                            >
                              {busy ? '処理中…' : '再開'}
                            </button>
                          )}
                          {!startable && !cancelable && !reservedCancel && (
                            <span className="text-ink-3">—</span>
                          )}
                        </ZebraTable.Td>
                      </ZebraTable.Row>
                    );
                  })
                )}
              </tbody>
            </ZebraTable>
          </div>
        )}
      </section>
    </main>
  );
}
